const MAP_SELECTOR = '[data-variant-media-map]';
const GALLERY_SELECTOR = 'media-gallery[data-product-id]';

class InstantVariantMedia {
  constructor() {
    this.onChange = this.onChange.bind(this);
    this.onInstantRequest = this.onInstantRequest.bind(this);

    document.addEventListener('change', this.onChange, true);
    document.addEventListener('instant-variant-media:select', this.onInstantRequest);
    this.preloadAll();
  }

  onChange(event) {
    if (!(event.target instanceof Element)) return;

    const target = event.target;
    const section = target.closest('.shopify-section, dialog');
    if (!section) return;

    const isBundleSelect = target.matches('.bundle-offer__slot-select');
    const picker = target.closest('variant-picker, swatches-variant-picker-component');

    if (!isBundleSelect && !picker) return;

    const immediateVariantId = this.getVariantId(target, picker);

    if (immediateVariantId) {
      this.selectVariant(section, immediateVariantId);
      return;
    }

    queueMicrotask(() => {
      const fallbackVariantId = this.getVariantId(target, picker);
      if (fallbackVariantId) this.selectVariant(section, fallbackVariantId);
    });
  }

  onInstantRequest(event) {
    const detail = event.detail || {};
    const section =
      event.target instanceof Element
        ? event.target.closest('.shopify-section, dialog')
        : null;

    if (!section || !detail.variantId) return;
    this.selectVariant(section, detail.variantId);
  }

  getVariantId(target, picker) {
    if (target.matches('.bundle-offer__slot-select')) {
      return target.value || null;
    }

    if (target instanceof HTMLInputElement) {
      return (
        target.dataset.variantId ||
        target.closest('[data-variant-id]')?.dataset.variantId ||
        null
      );
    }

    if (target instanceof HTMLSelectElement) {
      return (
        target.selectedOptions?.[0]?.dataset.variantId ||
        target.dataset.variantId ||
        null
      );
    }

    return (
      target.dataset.variantId ||
      picker?.selectedOption?.dataset?.variantId ||
      picker?.querySelector('input:checked[data-variant-id]')?.dataset.variantId ||
      picker?.querySelector('option:checked[data-variant-id]')?.dataset.variantId ||
      null
    );
  }

  selectVariant(section, variantId) {
    const galleries = Array.from(section.querySelectorAll(GALLERY_SELECTOR));

    for (const gallery of galleries) {
      const entry = this.getMap(gallery)?.[String(variantId)];
      if (!entry?.mediaId) continue;

      this.preload(entry.image);
      this.selectGalleryMedia(gallery, String(entry.mediaId));
    }
  }

  selectGalleryMedia(gallery, mediaId) {
    const slideshow = gallery.slideshow || gallery.querySelector('slideshow-component');

    if (slideshow?.select) {
      slideshow.select({ id: mediaId }, undefined, { animate: false });
    } else {
      customElements.whenDefined('slideshow-component').then(() => {
        const readySlideshow =
          gallery.slideshow || gallery.querySelector('slideshow-component');

        readySlideshow?.select?.(
          { id: mediaId },
          undefined,
          { animate: false }
        );
      });
    }

    const gridItem = gallery.querySelector(
      `.media-gallery__grid > [data-media-id="${CSS.escape(mediaId)}"]`
    );

    if (gridItem && gallery.dataset.presentation === 'grid') {
      gridItem.scrollIntoView({
        behavior: 'instant',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }

  getMap(gallery) {
    if (gallery.__instantVariantMediaMap) {
      return gallery.__instantVariantMediaMap;
    }

    try {
      gallery.__instantVariantMediaMap = JSON.parse(
        gallery.querySelector(MAP_SELECTOR)?.textContent || '{}'
      );
    } catch {
      gallery.__instantVariantMediaMap = {};
    }

    return gallery.__instantVariantMediaMap;
  }

  preloadAll() {
    const run = () => {
      document.querySelectorAll(GALLERY_SELECTOR).forEach((gallery) => {
        const map = this.getMap(gallery);
        Object.values(map).forEach((entry) => this.preload(entry?.image));
      });
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(run, { timeout: 1200 });
    } else {
      setTimeout(run, 250);
    }
  }

  preload(url) {
    if (!url) return;

    const image = new Image();
    image.decoding = 'async';
    image.fetchPriority = 'high';
    image.src = url;
  }
}

if (!window.__instantVariantMedia) {
  window.__instantVariantMedia = new InstantVariantMedia();
}